// src/pages/Participant.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { ref, get, set, update, onValue } from "firebase/database";
import { auth, db } from "../firebase";
import QRCode from "qrcode";
import { fmtTime } from "../utils/slots";
import { getRollFromEmail, getPreferredEmailForRoll } from "../utils/identity";

/** helpers */
function randCode() {
  return String(Math.floor(100000 + Math.random() * 900000));
}
function isActiveSlot(slot) {
  const now = Date.now();
  return slot?.st && slot?.et && now >= slot.st && now <= slot.et;
}
function safeStr(v) {
  return (v ?? "").toString();
}
function initials(name) {
  const n = safeStr(name).trim();
  if (!n) return "👤";
  const parts = n.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}
function simpleRoomLabel(roomObj, roomId) {
  if (!roomObj) return roomId || "-";
  return roomObj.n || roomObj.name || roomId || "-";
}

export default function Participant() {
  const [user, setUser] = useState(() => auth.currentUser || null);
  const [roll, setRoll] = useState("");
  const [member, setMember] = useState(null);

  // slots
  const [slots, setSlots] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);

  // team & rooms
  const [teamsMap, setTeamsMap] = useState({});
  const [roomsMap, setRoomsMap] = useState({});
  const [teamId, setTeamId] = useState("");
  const [teamCard, setTeamCard] = useState(null); // { id, name, roomLabel, table, members: [] }

  // broadcast message
  const [broadcastMsg, setBroadcastMsg] = useState("");

  // attendance / QR
  const [status, setStatus] = useState("");
  const [code, setCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");

  // ui
  const [loading, setLoading] = useState(true);
  const resolvingRef = useRef(false);

  // --- Auth ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, []);

  // --- Derive Roll from email ---
  useEffect(() => {
    (async () => {
      if (!user?.email || resolvingRef.current) return;
      resolvingRef.current = true;
      try {
        const derivedRoll = await getRollFromEmail(user.email);
        if (derivedRoll) setRoll(derivedRoll.toUpperCase());
      } finally {
        resolvingRef.current = false;
      }
    })();
  }, [user]);

  // --- Load member & patch email if needed ---
  useEffect(() => {
    if (!roll) return;
    (async () => {
      setLoading(true);
      const path = `membersByRoll/${roll}`;
      const snap = await get(ref(db, path));
      const preferredEmail = await getPreferredEmailForRoll(roll);

      if (!snap.exists()) {
        const minimal = {
          roll,
          name: roll,
          branch: "",
          section: "",
          teamId: "",
          email: preferredEmail || (user?.email || ""),
        };
        await set(ref(db, path), minimal);
        setMember(minimal);
      } else {
        const m = snap.val();
        let next = { ...m };
        if (preferredEmail && m.email !== preferredEmail) {
          await update(ref(db, path), { email: preferredEmail });
          next.email = preferredEmail;
        } else if (!m.email && user?.email) {
          await update(ref(db, path), { email: user.email });
          next.email = user.email;
        }
        setMember(next);
      }
      setLoading(false);
    })();
  }, [roll, user?.email]);

  // --- Subscribe slots ---
  useEffect(() => {
    const off = onValue(ref(db, "slots"), (snap) => {
      const data = snap.val() || {};
      const arr = Object.entries(data)
        .map(([id, s]) => ({ id, ...s }))
        .sort((a, b) => (a.st || 0) - (b.st || 0));
      setSlots(arr);
      const act = arr.find((s) => isActiveSlot(s));
      setActiveSlot(act || null);
    });
    return () => off();
  }, []);

  // --- Subscribe teams & rooms ---
  useEffect(() => {
    const offT = onValue(ref(db, "teams"), (snap) => setTeamsMap(snap.val() || {}));
    const offR = onValue(ref(db, "rooms"), (snap) => setRoomsMap(snap.val() || {}));
    return () => { offT(); offR(); };
  }, []);

  // --- Resolve team membership (by member.teamId or scan teams) ---
  useEffect(() => {
    if (!member) return;

    // resolve teamId
    let tid = safeStr(member.teamId);
    if (!tid) {
      const rl = member.roll || roll;
      const lc = rl?.toLowerCase();
      const uc = rl?.toUpperCase();

      Object.entries(teamsMap).some(([id, t]) => {
        if (t?.m && (t.m[rl] || t.m[lc] || t.m[uc])) {
          tid = id;
          return true;
        }
        return false;
      });
    }
    setTeamId(tid);

    // build card if available
    if (tid && teamsMap[tid]) {
      const tObj = teamsMap[tid];
      const roomId = tObj.room || "";
      const rLabel = simpleRoomLabel(roomsMap[roomId], roomId);
      const table = tObj.tb || "";
      const membersArr = Object.entries(tObj.m || {}).map(([r, d]) => ({
        roll: r,
        name: d?.n || r,
        branch: d?.b || "",
        section: d?.s || "",
        email: d?.e || "",
        mobile: d?.m || "",
      }));
      setTeamCard({
        id: tid,
        name: tObj.n || tid,
        roomLabel: rLabel,
        table,
        members: membersArr,
      });
    } else {
      setTeamCard(null);
    }
  }, [member, teamsMap, roomsMap, roll]);

  // --- Broadcast message (supports a few common shapes) ---
  useEffect(() => {
    // Try /broadcast first
    const offA = onValue(ref(db, "broadcast"), (snap) => {
      const v = snap.val();
      if (!v) return;
      const msg =
        v.participant ||
        v.participantMessage ||
        v.message ||
        v.msg ||
        "";
      if (msg) setBroadcastMsg(String(msg));
    });

    // Fallback: plain /message (if present)
    const offB = onValue(ref(db, "message"), (snap) => {
      const v = snap.val();
      if (!v) return;
      if (!broadcastMsg && typeof v === "string") {
        setBroadcastMsg(v);
      } else if (!broadcastMsg && typeof v?.participant === "string") {
        setBroadcastMsg(v.participant);
      }
    });

    return () => { offA(); offB(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Check attendance for the active slot to show status ---
  useEffect(() => {
    if (!roll || !activeSlot) return;
    (async () => {
      const attSnap = await get(ref(db, `attendance/${roll}/${activeSlot.id}`));
      setStatus(
        attSnap.exists()
          ? "Already checked in for this slot ✅"
          : "Not checked in yet."
      );
    })();
  }, [roll, activeSlot]);

  // --- Generate QR + code (only for active slot) ---
  async function generateCode(slotId) {
    if (!roll || !slotId) return;
    // ensure slotId is the active one
    if (!activeSlot || activeSlot.id !== slotId) return;
    const codeRef = ref(db, `codes/${roll}/${slotId}`);
    const codeSnap = await get(codeRef);
    let c = codeSnap.exists() ? codeSnap.val()?.code : randCode();
    if (!codeSnap.exists()) {
      await set(codeRef, { code: c, createdAt: Date.now() });
    }
    setCode(c);

    const url = new URL(window.location.origin + "/volunteer/checkin");
    url.searchParams.set("roll", roll);
    url.searchParams.set("slot", slotId);
    const dataUrl = await QRCode.toDataURL(url.toString());
    setQrDataUrl(dataUrl);
  }

  // --- UI ---

  if (!user) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow p-6 space-y-4">
        <h1 className="text-2xl font-bold">Participant — Attendance</h1>
        <p className="text-sm text-gray-600">
          Please sign in with Google to continue.
        </p>
      </div>
    );
  }

  const activeSlotId = activeSlot?.id;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Participant — Attendance</h1>

      {/* Broadcast banner + wishes */}
      {(broadcastMsg || true) && (
        <div className="rounded-2xl p-4 border bg-gradient-to-r from-amber-50 to-emerald-50">
          {broadcastMsg ? (
            <div className="text-sm text-gray-800">{broadcastMsg}</div>
          ) : null}
          <div className="mt-1 text-sm font-semibold text-emerald-700">
            All the best from Festum Team for Hardware Hackathon 2k25R🎉
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow p-6 space-y-4">
        {loading && (
          <div className="text-sm text-gray-500">Loading your details…</div>
        )}

        {!loading && member && (
          <>
            {/* Team card (nice UI at the top) */}
            <div className="rounded-2xl border p-4 bg-gray-50">
              <div className="flex flex-wrap items-start gap-4">
                {/* Team badge */}
                <div className="w-12 h-12 rounded-2xl bg-black text-white flex items-center justify-center text-lg font-bold">
                  {initials(teamCard?.name || member?.name || member?.roll)}
                </div>

                <div className="flex-1 min-w-[220px]">
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-lg font-semibold">
                      {teamCard?.name || "No Team Assigned"}
                    </div>
                    {teamCard?.roomLabel && (
                      <span className="text-xs px-2 py-1 rounded-lg border bg-white">
                        Room: <b className="font-mono">{teamCard.roomLabel}</b>
                      </span>
                    )}
                    {teamCard?.table && (
                      <span className="text-xs px-2 py-1 rounded-lg border bg-white">
                        Table: <b className="font-mono">{teamCard.table}</b>
                      </span>
                    )}
                  </div>

                  {/* Participant basic info */}
                  <div className="text-sm text-gray-600 mt-1">
                    <span className="font-medium">{member.name || member.roll}</span>
                    <span className="mx-1">•</span>
                    <span className="font-mono">{member.roll}</span>
                    {member.branch ? <span className="mx-1">• {member.branch}</span> : null}
                    {member.section ? <span className="mx-1">• {member.section}</span> : null}
                    {member.email ? <span className="mx-1">• {member.email}</span> : null}
                  </div>

                  {/* Team members list (first few inline, rest folded) */}
                  <div className="mt-3">
                    <div className="text-xs font-medium text-gray-700 mb-1">
                      Team Members
                    </div>
                    {teamCard?.members?.length ? (
                      <div className="flex flex-wrap gap-2">
                        {teamCard.members.slice(0, 6).map((m) => (
                          <div
                            key={m.roll}
                            className="flex items-center gap-2 px-2 py-1 rounded-xl border bg-white"
                          >
                            <div className="w-6 h-6 rounded-lg bg-gray-800 text-white flex items-center justify-center text-[10px] font-bold">
                              {initials(m.name || m.roll)}
                            </div>
                            <div className="text-xs">
                              <div className="font-medium">{m.name || m.roll}</div>
                              <div className="text-[11px] text-gray-500 font-mono">{m.roll}</div>
                            </div>
                          </div>
                        ))}
                        {teamCard.members.length > 6 && (
                          <div className="text-xs text-gray-600 self-center">
                            +{teamCard.members.length - 6} more
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        No members listed for this team.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Active slot status card (if any) */}
            {activeSlot && (
              <div className="p-4 rounded-xl border bg-white">
                <div className="font-medium">Current Active Slot</div>
                <div className="text-gray-600 text-sm">
                  {activeSlot.st ? fmtTime(activeSlot.st) : "?"} –{" "}
                  {activeSlot.et ? fmtTime(activeSlot.et) : "?"}
                </div>
                <div
                  className={`mt-2 text-sm ${
                    status.includes("Already") ? "text-green-700" : "text-amber-700"
                  }`}
                >
                  {status}
                </div>
              </div>
            )}

            {/* Slots list */}
            <div className="p-4 rounded-xl border bg-white">
              <div className="font-medium mb-2">Available Slots</div>
              <div className="space-y-2">
                {slots.length === 0 && (
                  <div className="text-sm text-gray-500">No slots available.</div>
                )}
                {slots.map((s) => {
                  const active = isActiveSlot(s);
                  return (
                    <div
                      key={s.id}
                      className={`p-3 rounded-lg border ${
                        active
                          ? "border-green-500 bg-green-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold">
                            {s.n || `Slot ${s.id}`}
                          </div>
                          <div className="text-xs text-gray-600">
                            {s.st ? fmtTime(s.st) : "?"} – {s.et ? fmtTime(s.et) : "?"}
                          </div>
                        </div>
                        {active ? (
                          <button
                            onClick={() => generateCode(s.id)}
                            className="px-3 py-2 rounded-xl border bg-white hover:bg-gray-100 text-sm"
                          >
                            Generate / Show Code
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Code + QR (only when user generated for active slot) */}
            {activeSlotId && code && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 rounded-2xl border bg-gray-50 flex flex-col items-center">
                  <div className="text-sm text-gray-600 mb-1">Your one-time code</div>
                  <div className="text-3xl font-mono font-bold tracking-widest">
                    {code}
                  </div>
                </div>
                <div className="p-4 rounded-2xl border bg-gray-50 flex flex-col items-center">
                  <div className="text-sm text-gray-600 mb-2">
                    Volunteer scans this
                  </div>
                  {qrDataUrl && (
                    <img src={qrDataUrl} alt="QR" className="w-56 h-56" />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
